"use client";
import postComment, { CommentResponseType } from "@/app/actions/post-comment";
import { Checkbox } from "@/components/checkbox";
import { DisabledTooltip } from "@/components/tooltip";
import { CommentSolidIcon, ShareSolidIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { useCidadao } from "@/providers/cidadao-provider";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ChangeEvent, FormEvent, useMemo, useState } from "react";

export type CommentAreaProps = {
  problema: number;
};

export function CommentArea({ problema }: CommentAreaProps) {
  const [checked, setChecked] = useState(false);
  const [popCheckbox, setPopCheckbox] = useState(false);
  const [popText, setPopText] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isSubimitting, setIsSubimitting] = useState(false);
  const checkboxErrorMessage =
    "Você deve concordar com os termos de uso para compartilhar seu comentário.";
  const textErrorMessage = "Você deve escrever um relato antes de enviar.";
  const [comment, setComment] = useState<string>("");
  const { cidadao } = useCidadao();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const previousPath = pathname.match(/.*(?=\/problemas)/)?.[0] ?? "/votacao";

  const textChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  const submitForm = async (event: FormEvent<HTMLFormElement>) => {
    setIsSubimitting(true);
    event.preventDefault();
    if (!cidadao) return;

    const response = await postComment({
      id_gov: cidadao.id_gov,
      id_problema: problema,
      descricao: comment,
    });

    const status =
      response.resultado.toLocaleLowerCase() as CommentResponseType;
    if (status === "sucesso") {
      router.replace(`${previousPath}?envio=sucesso`, { scroll: false });
    } else {
      const queryParams = new URLSearchParams(searchParams.toString());
      queryParams.set("envio", status);
      router.replace(`?${queryParams.toString()}`, { scroll: false });
    }
    setIsSubimitting(false);
  };

  const handleClick = () => {
    if (comment.length === 0) {
      setShowError(true);
      setPopText((prev) => !prev);
    } else if (!checked) {
      setShowError(true);
      setPopCheckbox((prev) => !prev);
    }
  };

  const enabled = comment.length > 0 && checked;
  const tooltipText = useMemo(() => {
    if (comment.length === 0) return textErrorMessage;
    if (!checked) return checkboxErrorMessage;
    return undefined;
  }, [checked, comment.length]);

  return (
    <form
      className="flex flex-col bg-secondary -mt-10 w-full"
      onSubmit={submitForm}
    >
      <div className="flex gap-2 px-8 py-2 font-semibold text-white text-sm">
        <span className="flex bg-primary p-1 rounded-2xl w-fit">
          <CommentSolidIcon className="fill-white" width={14} />
        </span>{" "}
        Relatos
      </div>
      <div className="flex flex-col gap-1 bg-white px-2 py-3 rounded-t-lg text-xs">
        <div className="flex">
          <textarea
            autoFocus
            rows={2}
            className={cn(
              "w-full p-4 border border-gray-300 rounded-4xl peer disabled:bg-gray-200 pr-9 disabled:text-gray-600",
              { "animate-stroke-color": popText }
            )}
            placeholder="Compartilhe como esse problema afeta a sua vida ou a da sua comunidade."
            value={comment}
            onChange={textChange}
          />
          <DisabledTooltip description={tooltipText} forceClosed={enabled}>
            <button
              type={enabled ? "submit" : "button"}
              className={cn(
                "-ml-9 hover:not-disabled:scale-125 active:not-disabled:scale-90 transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
                { "fill-gray-400": !enabled }
              )}
              onClick={handleClick}
              disabled={isSubimitting}
            >
              <ShareSolidIcon
                width={23}
                className={enabled ? undefined : "fill-gray-400"}
              />
            </button>
          </DisabledTooltip>
        </div>
        {showError && comment.length === 0 && (
          <span className="pl-6 text-primary">{textErrorMessage}</span>
        )}
      </div>
      <div className="flex flex-col gap-1 bg-muted px-8 py-2 pb-20 text-xs">
        <div className="flex items-center gap-1">
          <Checkbox
            onChange={() => setChecked(!checked)}
            checked={checked}
            label="Declaro que concordo com os"
            pop={popCheckbox}
          />{" "}
          <a
            href="https://portal.tcu.gov.br/sobre-o-portal/lgpd"
            className="underline hover:no-underline cursor-pointer"
          >
            termos de uso
          </a>
        </div>
        {showError && !checked && (
          <span className="text-primary">{checkboxErrorMessage}</span>
        )}
      </div>
    </form>
  );
}
